/**
 * Booking Time Constraint Utilities
 * 
 * Provides functions to check time-based restrictions on booking modifications
 * for student users. Students cannot edit/delete bookings that are:
 * - In the past
 * - Currently ongoing 
 * - Will start within the configured modification deadline (default: 1 hour)
 */

/**
 * Check if a booking is in the past (end time has passed)
 * @param {Object} booking - The booking object
 * @returns {boolean} True if booking has ended
 */
export function isBookingInPast(booking) {
	if (!booking?.endTime) return false;
	
	const now = new Date();
	const endTime = new Date(booking.endTime);
	
	return endTime <= now;
}

/**
 * Check if a booking is currently ongoing
 * @param {Object} booking - The booking object
 * @returns {boolean} True if booking is currently in progress
 */
export function isBookingOngoing(booking) {
	if (!booking?.startTime || !booking?.endTime) return false;
	
	const now = new Date();
	const startTime = new Date(booking.startTime);
	const endTime = new Date(booking.endTime);
	
	return startTime <= now && now < endTime;
}

/**
 * Check if a booking is starting soon (within specified seconds threshold)
 * @param {Object} booking - The booking object
 * @param {number} secondsThreshold - Seconds threshold (default: 3600 = 1 hour)
 * @returns {boolean} True if booking starts within threshold
 */
export function isBookingStartingSoon(booking, secondsThreshold = 3600) {
	if (!booking?.startTime) return false;
	
	const now = new Date();
	const startTime = new Date(booking.startTime);
	const thresholdTime = new Date(now.getTime() + (secondsThreshold * 1000));
	
	return startTime <= thresholdTime && startTime > now;
}

/**
 * Get minutes remaining until booking starts
 * @param {Object} booking - The booking object
 * @returns {number} Minutes until start (negative if already started/passed)
 */
export function getMinutesUntilStart(booking) {
	if (!booking?.startTime) return -1;
	
	const now = new Date();
	const startTime = new Date(booking.startTime);
	
	return Math.floor((startTime.getTime() - now.getTime()) / (1000 * 60));
}

/**
 * Get milliseconds remaining until booking starts
 * @param {Object} booking - The booking object
 * @returns {number} Milliseconds until start (0 if already started/passed)
 */
export function getMillisecondsUntilStart(booking) {
	if (!booking?.startTime) return 0;
	
	const now = new Date();
	const startTime = new Date(booking.startTime);
	const msUntilStart = startTime.getTime() - now.getTime();
	
	return Math.max(0, msUntilStart);
}

/**
 * Check if countdown timer should be shown for modification warnings
 * @param {Object} booking - The booking object
 * @param {string} userRole - Current user's role
 * @param {number} modificationDeadlineSeconds - Seconds before start when modification is no longer allowed
 * @param {number} warningAdvanceSeconds - Seconds before deadline to start showing warning (default: 900 = 15 min)
 * @returns {boolean} True if countdown should be shown
 */
export function shouldShowCountdown(
	booking, 
	userRole, 
	modificationDeadlineSeconds = 3600, 
	warningAdvanceSeconds = 900
) {
	if (userRole !== 'student' || !booking?.startTime) return false;
	
	const now = new Date();
	const startTime = new Date(booking.startTime);
	const deadlineTime = new Date(startTime.getTime() - (modificationDeadlineSeconds * 1000));
	const warningTime = new Date(deadlineTime.getTime() - (warningAdvanceSeconds * 1000));
	
	// Show countdown if we're between warning time and deadline time
	return now >= warningTime && now < deadlineTime;
}

/**
 * Get booking time constraint information
 * @param {Object} booking - The booking object
 * @param {string} userRole - Current user's role
 * @param {number} modificationDeadlineSeconds - Seconds before start time when modification is no longer allowed
 * @returns {Object} Constraint information { restricted, reason, minutesRemaining }
 */
export function getBookingTimeConstraint(booking, userRole, modificationDeadlineSeconds = 3600) {
	// Only apply time constraints to students
	if (userRole !== 'student') {
		return { restricted: false, reason: null, minutesRemaining: null };
	}
	
	if (!booking) {
		return { restricted: false, reason: null, minutesRemaining: null };
	}
	
	// Check if booking is in the past
	if (isBookingInPast(booking)) {
		return {
			restricted: true,
			reason: 'past',
			message: 'Diese Buchung liegt in der Vergangenheit und kann nicht mehr bearbeitet werden',
			minutesRemaining: 0
		};
	}
	
	// Check if booking is currently ongoing
	if (isBookingOngoing(booking)) {
		return {
			restricted: true,
			reason: 'ongoing',
			message: 'Diese Buchung läuft bereits und kann nicht mehr bearbeitet werden',
			minutesRemaining: 0
		};
	}
	
	// Check if booking is starting soon (within modification deadline)
	if (isBookingStartingSoon(booking, modificationDeadlineSeconds)) {
		const minutesRemaining = getMinutesUntilStart(booking);
		const deadlineMinutes = Math.ceil(modificationDeadlineSeconds / 60);
		return {
			restricted: true,
			reason: 'starting_soon',
			message: `Diese Buchung beginnt in weniger als ${deadlineMinutes} ${
			deadlineMinutes === 1 ? 'Minute' : 'Minuten'
		} und kann nicht mehr bearbeitet werden`,
			minutesRemaining: Math.max(0, minutesRemaining)
		};
	}
	
	// No restrictions
	const minutesRemaining = getMinutesUntilStart(booking);
	return {
		restricted: false,
		reason: null,
		message: null,
		minutesRemaining: Math.max(0, minutesRemaining)
	};
}

/**
 * Check if user can modify (edit/delete) a booking based on time constraints
 * @param {Object} booking - The booking object
 * @param {string} userRole - Current user's role
 * @param {number} modificationDeadlineSeconds - Seconds before start time when modification is no longer allowed
 * @returns {boolean} True if user can modify the booking
 */
export function canUserModifyBooking(booking, userRole, modificationDeadlineSeconds = 3600) {
	const constraint = getBookingTimeConstraint(booking, userRole, modificationDeadlineSeconds);
	return !constraint.restricted;
}

/**
 * Get countdown configuration for a booking
 * @param {Object} booking - The booking object
 * @param {string} userRole - Current user's role
 * @param {number} modificationDeadlineSeconds - Seconds before start time when modification is no longer allowed
 * @param {number} warningAdvanceSeconds - Seconds before deadline to start showing warning (default: 900 = 15 min)
 * @returns {Object|null} Countdown config with time and warning level, or null if no countdown needed
 */
export function getCountdownConfig(booking, userRole, modificationDeadlineSeconds = 3600, warningAdvanceSeconds = 900) {
	if (!shouldShowCountdown(booking, userRole, modificationDeadlineSeconds, warningAdvanceSeconds)) return null;
	
	const now = new Date();
	const startTime = new Date(booking.startTime);
	
	// Calculate time until modification deadline (when editing becomes restricted)
	const restrictionTime = new Date(startTime.getTime() - (modificationDeadlineSeconds * 1000));
	const msUntilRestriction = Math.max(0, restrictionTime.getTime() - now.getTime());
	const minutesUntilRestriction = Math.ceil(msUntilRestriction / (1000 * 60));
	
	// Determine warning level based on remaining time until restriction
	let warningLevel = 'info';
	const warningAdvanceMinutes = Math.ceil(warningAdvanceSeconds / 60);
	
	if (minutesUntilRestriction <= 5) {
		warningLevel = 'danger';
	} else if (minutesUntilRestriction <= warningAdvanceMinutes * 0.5) { // Half of warning advance time
		warningLevel = 'warning';
	}
	
	return {
		time: msUntilRestriction,
		minutesRemaining: minutesUntilRestriction,
		warningLevel,
		message: 'Änderungen nur noch möglich für:'
	};
}

/**
 * Check if a booking would be created/modified close to the modification deadline
 * @param {string} startDate - Date string (YYYY-MM-DD)
 * @param {string} startTime - Time string (HH:MM)
 * @param {string} userRole - Current user's role
 * @param {number} modificationDeadlineSeconds - Seconds before start when modification is no longer allowed
 * @param {number} warningThresholdSeconds - Seconds before deadline to show warning (default: 300 = 5 min)
 * @param {string} mode - 'create' or 'edit' to customize the message
 * @returns {Object|null} Warning info or null if no warning needed
 */
export function getCreationNearDeadlineWarning(
	startDate,
	startTime,
	userRole,
	modificationDeadlineSeconds = 3600,
	warningThresholdSeconds = 300,
	mode = 'create'
) {
	if (userRole !== 'student' || !startDate || !startTime) return null;

	const now = new Date();
	const bookingStart = new Date(`${startDate}T${startTime}`);
	const modificationDeadline = new Date(bookingStart.getTime() - (modificationDeadlineSeconds * 1000));
	const warningTime = new Date(modificationDeadline.getTime() - (warningThresholdSeconds * 1000));

	// Check if we're between warning time and deadline
	if (now >= warningTime && now < modificationDeadline) {
		const minutesUntilDeadline = Math.ceil((modificationDeadline.getTime() - now.getTime()) / (1000 * 60));
		const actionText = mode === 'create' ? 'angelegt' : 'bearbeitet';

		return {
			showWarning: true,
			minutesRemaining: minutesUntilDeadline,
			message: `Buchung kann nur noch ${minutesUntilDeadline} ${
				minutesUntilDeadline === 1 ? 'Minute' : 'Minuten'
			} ${actionText} werden. Danach ist keine Bearbeitung oder Stornierung mehr möglich.`,
			warningLevel: minutesUntilDeadline <= 2 ? 'danger' : 'warning'
		};
	}

	// Check if we're past the deadline (booking would be immediately non-modifiable)
	if (now >= modificationDeadline) {
		const deadlineMinutes = Math.ceil(modificationDeadlineSeconds / 60);

		return {
			showWarning: true,
			minutesRemaining: 0,
			message: `Aufgrund der Stonierungsfrist von ${deadlineMinutes} Minuten ist nach dem ${
				mode === 'create' ? 'Erstellen' : 'Bearbeiten'
			} keine weitere Änderung oder Stornierung möglich!`,
			warningLevel: 'danger'
		};
	}

	return null;
}

